import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthModal } from '../contexts/AuthModalContext';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { login as loginThunk } from '../features/auth/thunks/auth.thunk';
import { resetErrors } from '../features/auth/slices/auth.slice';
import { selectAuthLoading, selectAuthError, selectAuthUser } from '../features/auth/selectors/auth.selector';
import logoSmall from '../assets/images/logos/ba-brand-icon-colored.png';
import Button from './ui/Button';
import LoadingSpinner from './ui/LoadingSpinner';
import renewIcon from '../assets/images/pre/renew.svg';
import BaoModal from './ui/BaoModal';

import { getErrorMessage } from '../utils/helpers/errorHelper';

const LoginModal: React.FC = () => {
  const { isOpen, closeLoginModal, runOnSuccess } = useAuthModal();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppSelector(selectAuthLoading);
  const serverError = useAppSelector(selectAuthError);
  const user = useAppSelector(selectAuthUser);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const displayServerError = getErrorMessage(serverError);

  const canSubmit = email.trim().length > 0 && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) return;

    const result = await dispatch(
      loginThunk({ email: email.toLowerCase().trim(), password })
    );

    if (loginThunk.fulfilled.match(result)) {
      const payload = result.payload as { requires2FA?: boolean; user?: unknown };
      if (payload.requires2FA) {
        closeLoginModal();
        navigate('/login', { state: { from: 'auth-modal', email: email.toLowerCase().trim() } });
        return;
      }
      closeLoginModal();
      runOnSuccess();
    } else {
      setError(getErrorMessage(result.payload));
    }
  };

  // Auth state from Redux: if user is already logged in, close modal
  useEffect(() => {
    if (isOpen && user) {
      closeLoginModal();
    }
  }, [isOpen, user, closeLoginModal]);

  const displayLocalError = getErrorMessage(error);

  return (
    <BaoModal
      isOpen={isOpen}
      onClose={closeLoginModal}
      backdropClassName="fixed inset-0 z-[100] flex items-end justify-center px-3 pb-3 lg:items-center lg:p-4 bg-black/60 backdrop-blur-sm"
      panelClassName="relative w-full max-w-[480px] max-h-[90vh] overflow-hidden rounded-[30px] p-4 shadow-xl lg:max-h-none lg:overflow-visible lg:rounded-[20px] lg:p-6 bg-white"
    >
      <Button
        type="button"
        unstyled
        onClick={closeLoginModal}
        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-lg leading-none hover:opacity-80 lg:top-4 lg:right-4 lg:w-8 lg:h-8 lg:text-xl"
        style={{ color: '#BABABA' }}
        aria-label="Close"
      >
        ×
      </Button>

      <div className="relative text-center pt-5 mt-2 mb-2 lg:pt-0 lg:mt-6 lg:mb-4">
        <img
          src={logoSmall}
          alt="BaoAfrik"
          className="w-8 h-8 mx-auto object-contain lg:w-10 lg:h-10"
        />
        {(displayLocalError || displayServerError) && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            role="alert"
          >
            <div
              className="flex items-start gap-1.5 rounded-[16px] border px-2 py-1.5 shadow-sm min-w-[180px] max-w-[260px]"
              style={{ backgroundColor: '#FFFBFB', borderColor: '#FEC3C3' }}
            >
              <img src={renewIcon} alt="" className="w-8 h-8 shrink-0 mt-0.5" aria-hidden />
              <div className="flex-1 min-w-0 text-left">
                <p className="font-semibold text-xs leading-tight" style={{ fontFamily: 'Bricolage Grotesque, sans-serif', color: '#212121' }}>
                  {(displayLocalError || displayServerError).toLowerCase().includes('verify your email') ? 'Email Not Verified' : 'Login failed'}
                </p>
                <p className="text-[10px] mt-0.5 leading-tight" style={{ fontFamily: 'Poppins, sans-serif', color: '#939393' }}>
                  {displayLocalError || displayServerError}
                </p>
              </div>
              <Button
                type="button"
                unstyled
                onClick={() => {
                  setError('');
                  dispatch(resetErrors());
                }}
                className="shrink-0 w-8 h-8 flex items-center justify-center text-xl leading-none hover:opacity-80"
                style={{ color: '#6A6A6A' }}
                aria-label="Dismiss error"
              >
                ×
              </Button>
            </div>
          </div>
        )}
      </div>

      <h2
        id="login-modal-title"
        className="text-center text-base font-semibold mb-0.5 lg:text-lg lg:mb-1"
        style={{ fontFamily: 'Bricolage Grotesque, sans-serif', color: '#212121' }}
      >
        Please{' '}
        <span
          style={{
            fontFamily: 'Bricolage Grotesque, sans-serif',
            background: 'linear-gradient(91deg, #64B5F6 -9.42%, #1A237E 127.21%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          log in
        </span>{' '}
        to continue this activity.
      </h2>
      <p className="text-center text-[10px] mb-3 lg:text-xs lg:mb-5" style={{ color: '#B0B0B0' }}>
        Enter your information to access the platform.
      </p>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="mb-3 lg:mb-4">
          <label
            className="block text-[10px] font-medium mb-0.5 lg:text-xs lg:mb-1"
            style={{ fontFamily: 'Bricolage Grotesque, sans-serif', color: '#212121' }}
          >
            E-mail address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your mail address"
            disabled={isLoading}
            className="w-full px-2.5 py-2 border rounded-[10px] text-xs focus:outline-none focus:ring-1 placeholder:text-[#D9D9D9] lg:px-3 lg:py-2.5 lg:rounded-[12px] lg:text-sm"
            style={{
              backgroundColor: '#FFFFFF',
              borderColor: '#E4E4E4',
            }}
          />
        </div>

        <div className="mt-3 pb-4 lg:mt-5 lg:pb-6">
          <div className="flex items-center justify-between mb-0.5 lg:mb-1">
            <label
              className="block text-[10px] font-medium lg:text-xs"
              style={{ fontFamily: 'Bricolage Grotesque, sans-serif', color: '#212121' }}
            >
              Password
            </label>
            <Link
              to="/forgot-password"
              onClick={closeLoginModal}
              className="text-[10px] hover:opacity-90 lg:text-[11px]"
              style={{ color: '#64B5F6' }}
            >
              Forgotten password?
            </Link>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={isLoading}
              className="w-full px-2.5 py-2 pr-8 border rounded-[10px] text-xs focus:outline-none focus:ring-1 placeholder:text-[#D9D9D9] lg:px-3 lg:py-2.5 lg:pr-9 lg:rounded-[12px] lg:text-sm"
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: '#E4E4E4',
              }}
            />
            <Button
              type="button"
              unstyled
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
              style={{ color: '#E9E9E9' }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" style={{ color: '#6A6A6A' }}>
                {showPassword ? (
                  <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                ) : (
                  <>
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C2.732 4.943 6.522 3 10 3s7.268 1.943 9.542 7c-1.274 5.057-5.064 7-9.542 7S2.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </>
                )}
              </svg>
            </Button>
          </div>
        </div>

        <div className="pt-3 pb-3 lg:pt-4 lg:pb-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            disabled={!canSubmit || isLoading}
            loading={false}
            iconRight={isLoading ? <LoadingSpinner size="sm" color="white" className="shrink-0" /> : undefined}
            className={`!text-white !font-medium !rounded-[10px] !py-2 !px-4 text-xs lg:!rounded-[12px] lg:!py-2.5 lg:text-sm hover:!scale-100 active:!scale-100 ${isLoading ? '!bg-[#FDE4BB]' : canSubmit ? '!bg-[#F9A825] hover:!bg-[#e59720]' : '!bg-[#E9E9E9]'}`}
          >
            Sign In
          </Button>
        </div>
      </form>

      <div
        className="rounded-[10px] border py-1.5 px-2.5 text-center mb-2 lg:rounded-[12px] lg:py-2 lg:px-3 lg:mb-4"
        style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E4' }}
      >
        <span className="text-[11px] lg:text-[13px]" style={{ color: '#919191' }}>Don&apos;t have an account ? </span>
        <Link
          to="/register"
          onClick={closeLoginModal}
          className="font-medium underline hover:opacity-90 text-[11px] lg:text-[13px]"
          style={{ color: '#212121' }}
        >
          Sign Up
        </Link>
      </div>

      <div className="flex items-center justify-center gap-1 text-[10px] lg:text-xs pb-6 lg:pb-0" style={{ color: '#919191' }}>
        <Link to="/terms" onClick={closeLoginModal}>Terms of Use</Link>
        <span style={{ color: '#919191' }}>|</span>
        <Link to="/privacy" onClick={closeLoginModal}>Privacy Policies</Link>
        <span style={{ color: '#919191' }}>|</span>
        <Link to="/cookie-policy" onClick={closeLoginModal}>Cookie Policies</Link>
      </div>

      {/* Drag indicator - mobile only, at bottom */}
      <div className="flex justify-center pt-2 pb-2 lg:hidden" aria-hidden="true">
        <div
          className="h-1.5 w-20 shrink-0 rounded-full"
          style={{ backgroundColor: '#E9E9E9' }}
        />
      </div>
    </BaoModal>
  );
};

export default LoginModal;
